import React, { useEffect, useState } from "react";
import { useSession } from "@/hooks/useSession";
import { getSupabaseClient } from "@/lib/supabase/loader";
import { Navigate } from "react-router-dom";
import { APP_NAME } from "@/lib/constants";
import { CONSUMER_ROLES, ADMIN_ROLES, AppRole, hasAnyRole } from "@/lib/rbac";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface RequireRoleProps {
  allowed: AppRole[];
  children: React.ReactElement;
}

export default function RequireRole({ allowed, children }: RequireRoleProps) {
  const { user, isLoading } = useSession();
  const [roles, setRoles] = useState<AppRole[] | null>(null);
  const [loadingRoles, setLoadingRoles] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchRoles = async () => {
      if (!user) return setLoadingRoles(false);
      setLoadingRoles(true);
      try {
        // First, try to get roles from user metadata (already enriched by SessionProvider)
        const userMetadataRoles = (user?.user_metadata?.roles ?? []) as AppRole[];
        const appMetadataRoles = (user?.app_metadata?.roles ?? []) as AppRole[];
        
        // Combine both sources
        let combinedRoles = [...new Set([...userMetadataRoles, ...appMetadataRoles])];
        
        // Also check account_type to infer a base role when roles array is incomplete.
        // This handles the case where a partner user only has account_type='partner'
        // set in metadata but hasn't been assigned a granular role yet.
        const accountType = (user?.user_metadata?.account_type ?? user?.app_metadata?.account_type) as string | undefined;
        if (accountType === 'partner' && !combinedRoles.some(r => r.startsWith('partner_'))) {
          combinedRoles.push('partner_viewer' as AppRole);
        }
        if (accountType === 'institution' && !combinedRoles.some(r => r.startsWith('org_'))) {
          combinedRoles.push('org_admin' as AppRole);
        }
        
        // If no roles in metadata, fetch from database profiles table
        if (combinedRoles.length === 0) {
          const supabase = await getSupabaseClient();
          
          // Try security-definer RPC first (bypasses RLS during login)
          try {
            const { data: rpcResult, error: rpcError } = await supabase.rpc('get_user_roles', {
              user_id: user.id,
            });
            
            if (!rpcError && rpcResult) {
              const row = Array.isArray(rpcResult) ? rpcResult[0] : rpcResult;
              const dbRoles = Array.isArray(row?.roles) ? row.roles : [];
              const dbAccountType = row?.account_type as string | undefined;
              
              combinedRoles = dbRoles as AppRole[];
              
              // Infer role from account_type if roles still empty
              if (combinedRoles.length === 0 && dbAccountType === 'partner') {
                combinedRoles.push('partner_viewer' as AppRole);
              }
              if (combinedRoles.length === 0 && dbAccountType === 'institution') {
                combinedRoles.push('org_admin' as AppRole);
              }
            }
          } catch {
            // Fall back to direct table query
            const { data, error } = await supabase
              .from("profiles")
              .select("roles, account_type")
              .eq("id", user.id)
              .single();
            
            if (!error && data) {
              const r = data?.roles as unknown ?? [];
              combinedRoles = Array.isArray(r) ? (r as AppRole[]) : [];
              
              const dbAccType = data?.account_type as string | undefined;
              if (combinedRoles.length === 0 && dbAccType === 'partner') {
                combinedRoles.push('partner_viewer' as AppRole);
              }
              if (combinedRoles.length === 0 && dbAccType === 'institution') {
                combinedRoles.push('org_admin' as AppRole);
              }
            }
          }
        }
        
        if (mounted) {
          setRoles(combinedRoles.length > 0 ? combinedRoles : []);
        }
      } catch (err) {
        if (mounted) setRoles([]);
      } finally {
        if (mounted) setLoadingRoles(false);
      }
    };
    fetchRoles();
    return () => {
      mounted = false;
    };
  }, [user]);

  if (isLoading || loadingRoles) {
    return (
      <div className="p-8">
        <Card>
          <CardHeader>
            <CardTitle>Checking access</CardTitle>
          </CardHeader>
          <CardContent>Verifying permissions for {APP_NAME} admin console...</CardContent>
        </Card>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (!roles || (!hasAnyRole(roles, allowed) && !hasAnyRole(roles, ADMIN_ROLES))) {
    return (
      <div className="p-8">
        <Card>
          <CardHeader>
            <CardTitle>Unauthorized</CardTitle>
          </CardHeader>
          <CardContent>
            You do not have the required permissions to access this area. Allowed roles: {allowed.join(", ")}. Your roles: {roles?.join(", ") || "none"}
          </CardContent>
        </Card>
      </div>
    );
  }

  return children;
}
